import { baseApi } from "@/shared/api";
import type { SigningActionResponse } from "@/shared/types";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type {
  ServiceAgreementMSBResponse,
  ServiceAgreementMSBContract,
  ServiceAgreementMSBListResponse,
  ServiceAgreementMSBApiPayload,
} from "../types";

export const serviceAgreementMSBApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    submitServiceAgreementMSB: builder.mutation<ServiceAgreementMSBResponse, ServiceAgreementMSBApiPayload>({
      query: (body) => ({
        url: "/api/legal/service-agreements-msb/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ServiceAgreementsMSB" as const, id: "LIST" }],
    }),

    updateServiceAgreementMSB: builder.mutation<
      ServiceAgreementMSBResponse,
      { id: number; data: ServiceAgreementMSBApiPayload }
    >({
      query: ({ id, data }) => ({
        url: `/api/legal/service-agreements-msb/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ServiceAgreementsMSB" as const, id },
        { type: "ServiceAgreementsMSB" as const, id: "LIST" },
      ],
    }),

    getServiceAgreementMSB: builder.query<ServiceAgreementMSBResponse, number>({
      query: (id) => ({
        url: `/api/legal/service-agreements-msb/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "ServiceAgreementsMSB" as const, id }],
    }),

    getServiceAgreementMSBList: builder.query<ServiceAgreementMSBContract[], void>({
      query: () => ({
        url: "/api/legal/service-agreements-msb/",
        method: "GET",
      }),
      transformResponse: (response: ServiceAgreementMSBListResponse): ServiceAgreementMSBContract[] => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "ServiceAgreementsMSB" as const, id })),
              { type: "ServiceAgreementsMSB" as const, id: "LIST" },
            ]
          : [{ type: "ServiceAgreementsMSB" as const, id: "LIST" }],
    }),

    submitServiceAgreementMSBForSigning: builder.mutation<SigningActionResponse, { id: number; cms_signature: string; file_base64: string }>({
      query: ({ id, cms_signature, file_base64 }) => ({
        url: `/api/legal/service-agreements-msb/${id}/submit-for-signing/`,
        method: "POST",
        body: { cms_signature, file_base64 },
        credentials: "include" as const,
      }),
      invalidatesTags: () => [
        { type: "ServiceAgreementsMSB" as const, id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          serviceAgreementMSBApi.util.updateQueryData("getServiceAgreementMSB", id, (draft) => {
            draft.signing_status = 1;
          }),
        );
      },
    }),

    downloadServiceAgreementMSBPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/legal/service-agreements-msb/${id}/preview/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),

    deleteServiceAgreementMSB: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/service-agreements-msb/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ServiceAgreementsMSB" as const, id },
        { type: "ServiceAgreementsMSB" as const, id: "LIST" },
      ],
    }),

    revokeServiceAgreementMSB: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/service-agreements-msb/${id}/revoke/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ServiceAgreementsMSB" as const, id },
        { type: "ServiceAgreementsMSB" as const, id: "LIST" },
        "PendingActions",
      ],
    }),
  }),
});

export const {
  useSubmitServiceAgreementMSBMutation,
  useUpdateServiceAgreementMSBMutation,
  useGetServiceAgreementMSBQuery,
  useGetServiceAgreementMSBListQuery,
  useSubmitServiceAgreementMSBForSigningMutation,
  useDownloadServiceAgreementMSBPreviewMutation,
  useDeleteServiceAgreementMSBMutation,
  useRevokeServiceAgreementMSBMutation,
} = serviceAgreementMSBApi;
